/// <reference lib="dom" />
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { mergeUrlsWithParams, normalizeUrl } from '@sisense/sdk-common';
import { BaseAuthenticator } from './base-authenticator.js';
import { addQueryParamsToUrl } from './helpers.js';
import { errorInterceptor } from './interceptors.js';
import { TranslatableError } from './translation/translatable-error.js';
/**
 * Authenticator for handling SSO (Single Sign-On) authentication flow
 */
export class SsoAuthenticator extends BaseAuthenticator {
    constructor(url, enableSilentPreAuth = false, alternativeSsoHost = '') {
        super('sso');
        this.url = normalizeUrl(url, true);
        this._enableSilentPreAuth = enableSilentPreAuth;
        this._alternativeSsoHost = alternativeSsoHost;
    }
    /**
     * Attempts to authenticate the user via SSO.
     * If silent mode is enabled and supported, tries silent authentication first.
     *
     * @param silent - Whether to attempt silent authentication first
     * @returns Promise resolving to authentication result
     */
    authenticate(silent = true) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this._authenticating = true;
                const { isAuthenticated, loginUrl: authUrl } = yield this.checkAuthentication();
                if (isAuthenticated) {
                    this._resolve(true);
                    return yield this._result;
                }
                let finalLoginUrl = '';
                try {
                    // Check if authUrl is a valid absolute path URL (with http)
                    new URL(authUrl);
                    finalLoginUrl = authUrl;
                    // If authUrl is a relative path, combine it with the base URL
                    // If the user specifies an alternative host, prioritize using that host
                    // Otherwise, default to the connection's configured host
                }
                catch (_b) {
                    finalLoginUrl = mergeUrlsWithParams(this._alternativeSsoHost || this.url, authUrl);
                }
                finalLoginUrl = addQueryParamsToUrl(finalLoginUrl, { return_to: window.location.href });
                if (this._enableSilentPreAuth && silent) {
                    yield this.authenticateSilent(finalLoginUrl);
                    const { isAuthenticated } = yield this.checkAuthentication();
                    if (isAuthenticated) {
                        this._resolve(true);
                        return yield this._result;
                    }
                }
                (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.replace(finalLoginUrl);
            }
            finally {
                this._resolve(false);
                this._authenticating = false;
            }
            return this._result;
        });
    }
    /**
     * Attempts silent authentication using an invisible iframe
     *
     * @param loginUrl - The URL to load in the iframe
     */
    authenticateSilent(loginUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
            iframe.src = loginUrl;
            yield new Promise((resolve) => {
                iframe.onload = () => {
                    resolve(true);
                };
            });
            document.body.removeChild(iframe);
        });
    }
    /**
     * Checks the current authentication status with the server
     *
     * @returns Promise with authentication status and login URL if needed
     * @throws {TranslatableError} If authentication check fails
     */
    checkAuthentication() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const fetchUrl = mergeUrlsWithParams(this.url, 'api/auth/isauth');
            const response = yield fetch(fetchUrl, {
                headers: { Internal: 'true' },
                credentials: 'include',
            }).catch(errorInterceptor);
            // covers the case when isAuth returns 200 with html in the body
            // (i.e. redirect to /app/main for deleted user)
            if (response.status === 200 &&
                !((_b = (_a = response.headers) === null || _a === void 0 ? void 0 : _a.get('Content-Type')) === null || _b === void 0 ? void 0 : _b.includes('application/json'))) {
                throw new TranslatableError('errors.authFailed');
            }
            const result = yield response.json();
            if (!result.isAuthenticated) {
                if (!result.ssoEnabled) {
                    throw new TranslatableError('errors.ssoNotEnabled');
                }
                if (!result.loginUrl) {
                    throw new TranslatableError('errors.ssoNoLoginUrl');
                }
            }
            return {
                isAuthenticated: result.isAuthenticated,
                loginUrl: result.loginUrl || '',
            };
        });
    }
}
/**
 * Type guard to check if an authenticator is an SSO authenticator
 *
 * @param authenticator - The authenticator to check
 * @returns boolean indicating if the authenticator is an SSO authenticator
 * @internal
 */
export function isSsoAuthenticator(authenticator) {
    return authenticator.type === 'sso';
}
//# sourceMappingURL=sso-authenticator.js.map